import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Award, Zap } from 'lucide-react';

/**
 * Level Progress Component
 * Shows progress towards next affiliate level
 */
const LevelProgress = ({ currentLevel, nextLevel, paidReferrals }) => {
  if (!nextLevel) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border-2 border-amber-200"
      >
        <div className="flex items-center gap-3 mb-4">
          <Award className="text-amber-600" size={24} />
          <h3 className="text-xl font-bold text-amber-900">🎉 Niveau Maximum Atteint !</h3>
        </div>
        <p className="text-amber-800">
          Félicitations ! Tu es au niveau maximum avec {currentLevel?.commission_rate}% de commission.
          Continue à partager pour maximiser tes gains !
        </p>
      </motion.div>
    );
  }

  const referralsNeeded = nextLevel.referrals_needed || 0;
  const nextLevelMin = nextLevel.min_referrals || 0;
  const currentProgress = paidReferrals;
  const progressPercent = (currentProgress / nextLevelMin) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 rounded-xl p-6 border-2 border-purple-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="text-purple-600" size={24} />
          <h3 className="text-xl font-bold text-purple-900">Progression vers {nextLevel.name}</h3>
        </div>
        <span className="text-3xl">{nextLevel.badge}</span>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">{currentProgress} filleuls payants</span>
          <span className="text-purple-600 font-semibold">{nextLevelMin} requis</span>
        </div>
        <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(progressPercent, 100)}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full"
          />
        </div>
      </div>

      {/* Motivation message */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-purple-200"
      >
        <div className="flex items-start gap-3">
          <Zap className="text-orange-500 flex-shrink-0 mt-1" size={20} />
          <div>
            <p className="font-semibold text-purple-900 mb-1">
              🔥 Plus que {referralsNeeded} filleul{referralsNeeded > 1 ? 's' : ''} pour atteindre {nextLevel.name} !
            </p>
            <p className="text-sm text-gray-700">
              🎯 Objectif suivant : <span className="font-bold text-purple-600">{nextLevel.name}</span> — 
              tu gagnes <span className="font-bold">+{nextLevel.commission_rate - (currentLevel?.commission_rate || 10)}%</span> de commission 
              dès {nextLevel.min_referrals} filleuls payants !
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LevelProgress;
